import { isValid, format } from "date-fns";

import PunishText from "@/components/PunishText.vue";
import Avatar from "@/components/Avatar.vue";

import config from "@/config.js";
import {
  getSeverityColorHelper,
  getPunishNameHelper,
  getPunishmentIconNameHelper
} from "../../helpers/punishments";
import { sortByDate } from "../../helpers/sorting";

export default {
  components: { PunishText, Avatar },
  props: {
    username: {
      type: String,
      default: "MHF_Chicken"
    }
  },
  data: () => ({
    loading: false,
    highlightedPunishment: undefined,
    activeBans: undefined,
    avatar: undefined,
    formattedUsername: "",
    search: "",
    enableGroupBy: false,
    headers: [
      {
        text: "Type",
        value: "type",
        sort: (typeA, typeB) =>
          config.typeSortOrder.indexOf(typeA) -
          config.typeSortOrder.indexOf(typeB)
      },
      { text: "Executed by", value: "who_executed" },
      { text: "Reason", value: "reason" },
      {
        text: "Date issued",
        value: "time",
        sort: sortByDate
      },
      {
        text: "Expires",
        value: "expiration",
        sort: sortByDate
      }
    ],
    punishments: []
  }),
  computed: {
    paidUnbanLink() {
      switch (config.networkName) {
        case "sky":
          return "https://buy.cosmicsky.com/category/1337781";
        case "prisons":
          return "https://buy.cosmicprisons.com/category/1167436";
      }
    }
  },
  methods: {
    // Format the epoch date to something readable, and show meaningful texts for -1 and invalid dates.
    getFormattedDate(date) {
      if (date < 1) return "Never";
      if (!isValid(new Date(date))) return "Unknown";
      return format(new Date(date), "PPp");
    },

    hasExpiredPunishment(date) {
      if (date < 1 || !isValid(new Date(date))) return false;
      return new Date(date) < new Date();
    },

    // If the reason includes any of the noUnbanReasons strings, the unban should be blocked
    hasBlockedPaidUnbanReason(givenReason) {
      return config.noUnbanReasons.some(reason => {
        if (reason && givenReason) {
          return givenReason.toLowerCase().includes(reason.toLowerCase());
        } else return false;
      });
    },

    // Return true if the player bought an unban somewhere in the past year
    hasPurchasedUnbanLastYear(latestUnbanTime) {
      return latestUnbanTime > Date.now() - (365 * 24 * 60 * 60 * 1000);
    },

    hasActiveBan() {
      return this.activeBans > 0;
    },

    // save the unban record time with the highest unban time
    updateLatestUnbanTime(latestUnbanTime, punishment) {
      if (
        punishment.type == "PLAYER" &&
        punishment.who_executed == "Buycraft" &&
        punishment.time > latestUnbanTime
      ) {
        latestUnbanTime = punishment.time;
      }
      return latestUnbanTime;
    },

    eligibleForUnban() {
      // Check based on nextAllowedUnban (from 'unbans' database)
      if (
        this.punishments[0] &&
        this.punishments[0].nextAllowedUnban &&
        this.punishments[0].nextAllowedUnban > Date.now()
      ) {
        return false;
      }

      // Check if punishment is type BAN, check for one of the blocked reasons,
      // and check if the punishment is expired
      let latestUnbanTime = -1;
      let latestBan = null;
      this.punishments.forEach(punishment => {
        if (punishment.type == 'BAN' && (latestBan == null || latestBan.time < punishment.time)) {
          latestBan = punishment;
        }
        latestUnbanTime = this.updateLatestUnbanTime(latestUnbanTime, punishment);
      });
      return (this.hasActiveBan() && latestBan != null && !this.hasBlockedPaidUnbanReason(latestBan.reason) && !this.hasPurchasedUnbanLastYear(latestUnbanTime));
    },

    // Get the name of a material icon based on the punishment type
    getPunishmentIconName(type) {
      return getPunishmentIconNameHelper(type);
    },

    // Get material color classname(s) based on punishment type
    getSeverityColor(type) {
      return getSeverityColorHelper(type);
    },

    getPunishName(type) {
      return getPunishNameHelper(type);
    },

    hasMoreRecentPunishment(punishment) {
      if (!this.highlightedPunishment) return false;
      return punishment.time < this.highlightedPunishment.time;
    },

    // Find and set the highlight punishment if one is available
    // in reverse and sorted on type importance so most relevant punishment is where the text is displayed for
    setHighlightedPunishment() {
      this.punishments
        .sort((punA, punB) => {
          // if (punA.time === -1 || punB.time === -1) return 1;
          return punB.time - punA.time;
        })
        .sort(
          (punA, punB) =>
            config.typeSortOrder.indexOf(punA.type) -
            config.typeSortOrder.indexOf(punB.type)
        )
        .reverse()
        .forEach(punishment => {
          if (this.hasExpiredPunishment(punishment.expiration)) return;
          // if (this.hasMoreRecentPunishment(punishment)) return;
          this.highlightedPunishment = punishment;
        });
    },

    // Retrieve all punishments for given user from the API
    getPunishments() {
      this.loading = true;
      fetch(`${config.apiUrl}/${config.networkName}/player/${this.username}`)
        .then(response => {
          if (response.status !== 200) {
            console.error(response.status);
            return;
          }

          response.json().then(data => {
            this.punishments = data;
            this.setHighlightedPunishment();
            this.getActiveBansCount();
          });
        })
        .catch(err => console.error(err))
        .finally(() => {
          this.loading = false;

          // Set the username again; prettier to read if a uuid was inputted.
          if (this.punishments[0] && this.punishments[0].username) {
            this.formattedUsername = this.punishments[0].username;
          } else {
            this.formattedUsername = this.username;
          }
        });
    },

    getActiveBansCount() {
      let playerId;
      switch (config.networkName) {
        case "sky":
          playerId = this.punishments[0].player_id;
          break;
        case "prisons":
          playerId = this.punishments[0].uuid;
      }

      fetch(`${config.apiUrl}/${config.networkName}/active-bans/${playerId}`)
        .then(response => {
          if (response.status !== 200) {
            console.error(response.status);
            return;
          }

          response.json().then(data => {
            this.activeBans = data.active_bans;
          });
        })
        .catch(err => console.error(err));
    }
  },

  created() {
    this.getPunishments();
    this.avatar = `${config.minotarUrl}/${this.username}/32`;
  }
};
